import { Title } from '@solidjs/meta'
import { type Component, createSignal, For, onCleanup, onMount, Show } from 'solid-js'
import Alert from '~/components/Alert'
import Card from '~/components/Card'
import Chip from '~/components/Chip'
import Container from '~/components/Container'
import Icon from '~/components/Icon'

/**
 * Resuelve el valor real de una variable de color.
 *
 * Leer la variable con `getPropertyValue` no sirve: casi todas están escritas
 * como `color-mix(...)` y devolverían la fórmula en vez del color. Pintándola
 * en un elemento suelto, el navegador la calcula y ya se puede leer el `rgb()`.
 */
const resolveColor = (variable: string, tema: string): string => {
  // `tema` no se usa para calcular: se lee para que Solid vuelva a ejecutar esto
  // cuando cambia el tema, porque el mismo token da otro color en oscuro.
  void tema
  if (typeof document === 'undefined') return ''

  const probe = document.createElement('div')
  probe.style.cssText = `position:absolute;visibility:hidden;background-color:var(${variable})`
  document.body.appendChild(probe)
  const value = getComputedStyle(probe).backgroundColor
  probe.remove()
  return value
}

/**
 * Pasa a hexadecimal lo que devuelve el navegador, que no siempre es `rgb()`:
 * los tokens escritos con `color-mix` salen como `color(srgb 0.4 0.1 0.1)`, con
 * los canales de 0 a 1 en vez de 0 a 255.
 */
const toHex = (value: string): string => {
  const numeros = value.match(/[\d.]+/g)
  if (!numeros || numeros.length < 3) return value

  const escala = value.startsWith('color(') ? 255 : 1
  const [r, g, b] = numeros.slice(0, 3).map(n => Number(n) * escala)
  const alpha = numeros[3] !== undefined ? Number(numeros[3]) : 1

  const hex = `#${[r, g, b].map(n => Math.round(n).toString(16).padStart(2, '0')).join('')}`
  return alpha < 1 ? `${hex} · ${Math.round(alpha * 100)}%` : hex
}

// ── Datos ──────────────────────────────────────────────────────────────────

type TokenType = { clase: string; variable: string; uso: string }

const SUPERFICIES: TokenType[] = [
  { clase: 'bg-background', variable: '--semantic-background', uso: 'El fondo de la página, por detrás de todo' },
  { clase: 'bg-surface', variable: '--semantic-surface', uso: 'Tarjetas, modales y paneles que se levantan del fondo' },
  { clase: 'bg-surface-muted', variable: '--semantic-surface-muted', uso: 'Campos de formulario y bloques dentro de una tarjeta' },
  { clase: 'bg-surface-hover', variable: '--semantic-surface-hover', uso: 'Fila de una tabla o de una lista bajo el cursor' },
  { clase: 'border-border', variable: '--semantic-border', uso: 'Separadores y bordes de tarjeta' },
  { clase: 'border-border-strong', variable: '--semantic-border-strong', uso: 'Borde de un control: pide más contraste que un separador' }
]

const TEXTOS: TokenType[] = [
  { clase: 'text-foreground', variable: '--semantic-foreground', uso: 'Texto principal, títulos y valores' },
  { clase: 'text-foreground-muted', variable: '--semantic-foreground-muted', uso: 'Etiquetas y texto secundario' },
  { clase: 'text-foreground-subtle', variable: '--semantic-foreground-subtle', uso: 'Texto de ayuda, marcadores y notas al pie' }
]

const ESTADOS = [
  { nombre: 'primary', etiqueta: 'Primary', uso: 'La marca. Acción principal de cada pantalla' },
  { nombre: 'danger', etiqueta: 'Danger', uso: 'Errores de validación, borrados y avisos graves' },
  { nombre: 'success', etiqueta: 'Success', uso: 'Confirmaciones y estados completados' },
  { nombre: 'warning', etiqueta: 'Warning', uso: 'Avisos que piden atención pero no bloquean' },
  { nombre: 'info', etiqueta: 'Info', uso: 'Información neutra y estados en curso' }
] as const

const TONOS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const

const ESCALAS = [
  { nombre: 'primary', etiqueta: 'Primary', nota: 'Se genera entera a partir de un solo color: el de la marca de cada empresa' },
  { nombre: 'red', etiqueta: 'Red', nota: 'Base de `danger`' },
  { nombre: 'green', etiqueta: 'Green', nota: 'Base de `success`' },
  { nombre: 'blue', etiqueta: 'Blue', nota: 'Base de `info`' },
  { nombre: 'amber', etiqueta: 'Amber', nota: 'Base de `warning`' },
  { nombre: 'slate', etiqueta: 'Slate', nota: 'De ella salen fondos, bordes y textos en los dos temas' }
] as const

// ── Piezas de la página ────────────────────────────────────────────────────

/** Muestra de color con su clase y el valor que toma en el tema activo. */
const Muestra: Component<{ variable: string; clase: string; uso?: string; tema: string; alto?: string }> = props => (
  <div class="flex items-center gap-4">
    <div class={`shrink-0 rounded-xl border border-border shadow-sm ${props.alto ?? 'h-14 w-14'}`} style={{ 'background-color': `var(${props.variable})` }} />
    <div class="min-w-0 flex-1">
      <code class="block truncate font-mono text-foreground text-sm">{props.clase}</code>
      <span class="block font-mono text-foreground-subtle text-xs">{toHex(resolveColor(props.variable, props.tema))}</span>
      <Show when={props.uso}>
        <span class="mt-1 block text-foreground-muted text-sm">{props.uso}</span>
      </Show>
    </div>
  </div>
)

const ColorsPage: Component = () => {
  // El valor de cada token cambia con el tema; esta señal obliga a recalcular
  // las muestras cuando alguien le da al interruptor de la barra superior.
  const [tema, setTema] = createSignal('light')

  onMount(() => {
    const leerTema = () => setTema(document.documentElement.getAttribute('data-theme') ?? 'light')
    leerTema()

    const observer = new MutationObserver(leerTema)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] })
    onCleanup(() => observer.disconnect())
  })

  return (
    <>
      <Title>Colores - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mt-6 mb-10">
          <h1 class="mb-4 font-bold text-4xl text-foreground">Colores</h1>
          <p class="max-w-3xl text-foreground-subtle text-xl leading-relaxed">
            La paleta no se escribe a mano en ningún componente: se nombra por su función. Un botón de borrar no es rojo, es <code>danger</code>, y por eso se lee igual en tema claro que en oscuro y
            cambia solo cuando una empresa configura su marca.
          </p>
          <div class="mt-4 flex flex-wrap items-center gap-2">
            <Chip color="primary" variant="soft" size="sm" class="shrink-0 whitespace-nowrap">
              Tema activo: {tema() === 'dark' ? 'oscuro' : 'claro'}
            </Chip>
            <span class="text-foreground-subtle text-sm">Los valores de abajo se recalculan al cambiarlo desde la barra superior.</span>
          </div>
        </div>

        <div class="space-y-12">
          {/* ── Cómo se usan ─────────────────────────────────────────────── */}
          <Alert variant="info" title="Nombra la función, no el color">
            <p class="text-sm">
              Usa <code>text-danger</code>, no <code>text-red-500</code>. Los tokens semánticos ya traen su versión oscura; un color crudo se queda igual en los dos temas y acaba ilegible sobre fondo
              oscuro. La única excepción son las escalas del final, para cuando hace falta un tono concreto.
            </p>
          </Alert>

          {/* ── Superficies ──────────────────────────────────────────────── */}
          <Card>
            <Card.Header
              title="Superficies y bordes"
              subtitle="Las capas sobre las que se apoya todo lo demás, del fondo de la página al borde de un campo."
              icon={<Icon icon="material-symbols-light:layers-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <For each={SUPERFICIES}>{token => <Muestra variable={token.variable} clase={token.clase} uso={token.uso} tema={tema()} />}</For>
              </div>
            </Card.Body>
          </Card>

          {/* ── Texto ────────────────────────────────────────────────────── */}
          <Card>
            <Card.Header
              title="Texto"
              subtitle="Tres niveles de énfasis. Bajar de nivel es la forma de jerarquizar sin cambiar de tamaño ni de peso."
              icon={<Icon icon="material-symbols-light:text-fields" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-6">
                <div class="rounded-xl border border-border bg-surface-muted p-6">
                  <p class="text-foreground text-lg">Texto principal — títulos, nombres y cifras</p>
                  <p class="text-foreground-muted">Texto secundario — etiquetas y descripciones de apoyo</p>
                  <p class="text-foreground-subtle text-sm">Texto sutil — ayudas, marcadores de posición y notas</p>
                </div>

                <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <For each={TEXTOS}>{token => <Muestra variable={token.variable} clase={token.clase} uso={token.uso} tema={tema()} />}</For>
                </div>

                <div class="flex items-center gap-4 rounded-xl bg-inverse p-6">
                  <Icon icon="material-symbols-light:invert-colors" size={24} class="text-on-inverse" />
                  <div>
                    <code class="block font-mono text-on-inverse text-sm">bg-inverse · text-on-inverse</code>
                    <span class="text-on-inverse/80 text-sm">El par invertido: oscuro sobre tema claro y claro sobre tema oscuro. Es el color `default` de los botones.</span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* ── Estados ──────────────────────────────────────────────────── */}
          <Card>
            <Card.Header
              title="Colores de estado"
              subtitle="Cada uno viene en tres piezas que se usan juntas: el sólido, el fondo tenue y el texto que va encima de ese fondo."
              icon={<Icon icon="material-symbols-light:palette-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-8">
                <For each={ESTADOS}>
                  {estado => (
                    <div class="rounded-xl border border-border p-5">
                      <div class="mb-4 flex flex-wrap items-baseline justify-between gap-2">
                        <h4 class="font-bold text-foreground text-lg capitalize">{estado.etiqueta}</h4>
                        <span class="text-foreground-muted text-sm">{estado.uso}</span>
                      </div>

                      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <Muestra variable={`--semantic-${estado.nombre}`} clase={`bg-${estado.nombre}`} uso="Relleno sólido: botones y bordes de error" tema={tema()} />
                        <Muestra variable={`--semantic-${estado.nombre}-soft`} clase={`bg-${estado.nombre}-soft`} uso="Fondo tenue: avisos y distintivos" tema={tema()} />
                        <Muestra variable={`--semantic-on-${estado.nombre}-soft`} clase={`text-on-${estado.nombre}-soft`} uso="El texto que se lee sobre el fondo tenue" tema={tema()} />
                      </div>

                      <div class="mt-4 flex flex-wrap items-center gap-3">
                        <span class="rounded-lg px-3 py-1.5 font-medium text-sm" style={{ 'background-color': `var(--semantic-${estado.nombre})`, color: 'white' }}>
                          Sólido
                        </span>
                        <span
                          class="rounded-lg px-3 py-1.5 font-medium text-sm"
                          style={{ 'background-color': `var(--semantic-${estado.nombre}-soft)`, color: `var(--semantic-on-${estado.nombre}-soft)` }}
                        >
                          Tenue con su texto
                        </span>
                      </div>
                    </div>
                  )}
                </For>
              </div>
            </Card.Body>
          </Card>

          {/* ── Escalas ──────────────────────────────────────────────────── */}
          <Card>
            <Card.Header
              title="Escalas completas"
              subtitle="Del 50 al 900. Sólo cuando hace falta un tono concreto que ningún token semántico cubre."
              icon={<Icon icon="material-symbols-light:gradient" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-8">
                <For each={ESCALAS}>
                  {escala => (
                    <div>
                      <div class="mb-3 flex flex-wrap items-baseline gap-x-3">
                        <h4 class="font-bold text-foreground">{escala.etiqueta}</h4>
                        <span class="text-foreground-subtle text-sm">{escala.nota}</span>
                      </div>
                      <div class="grid grid-cols-5 gap-2 md:grid-cols-10">
                        <For each={TONOS}>
                          {tono => (
                            <div class="min-w-0">
                              <div class="h-14 rounded-lg border border-border shadow-sm" style={{ 'background-color': `var(--color-${escala.nombre}-${tono})` }} />
                              <span class="mt-1 block text-center font-mono text-foreground-muted text-xs">{tono}</span>
                              <span class="block truncate text-center font-mono text-[10px] text-foreground-subtle">{toHex(resolveColor(`--color-${escala.nombre}-${tono}`, tema()))}</span>
                            </div>
                          )}
                        </For>
                      </div>
                    </div>
                  )}
                </For>
              </div>
            </Card.Body>
          </Card>

          {/* ── Marca por empresa ────────────────────────────────────────── */}
          <Card>
            <Card.Header
              title="La marca de cada empresa"
              subtitle="Toda la escala primary sale de una sola variable."
              icon={<Icon icon="material-symbols-light:storefront-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <p class="mb-4 text-foreground-muted">
                El color de arranque es <code>--main-color</code> (por defecto <code>#d13a44</code>). De él se derivan los diez tonos mezclando con blanco y con negro, y de ahí salen
                <code> primary</code>, <code>primary-soft</code> y el resto. Cambiar esa única variable repinta la interfaz entera.
              </p>
              <pre class="overflow-x-auto rounded-xl border border-border bg-surface-muted p-4 text-sm">
                <code>{`:root {\n  --florez-main-color: #0d6efd;\n}`}</code>
              </pre>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default ColorsPage
